<script lang="ts">
    import BaseTab from "@/tabs/mixins/BaseTab";
    import tabIcon from "@/assets/gear.svg";
    export default {
        extends: BaseTab,
        name: "ListResurs",
        tabName: "Resurser",
        icon: tabIcon,
    };
</script>

<script setup lang="ts">
    import { computed, onBeforeMount, onBeforeUnmount, ref, toRefs } from "vue";
    import { useStore } from "@/store";
    import PlusCircleBlueIcon from "@/assets/plus_circle_blue.svg";
    import CellInaktiv from "@/components/Table/CellInaktiv.vue";
    import EditIcon from "@/assets/actionIcons/edit.svg";
    import AdminLockBorder from "@/components/AdminLockBorder.vue";
    import HollowButton from "@/components/widgets/HollowButton.vue";
    import KTable from "@/components/KTable/KTable.vue";
    import { KTableAction } from "@/components/KTable/types";
    import { Resurs as ResursType } from "@/components/forms/reusables/resurserHelper";
    import Resurs from "@/tabs/Operation/Admin/Resurs.vue";

    const store = useStore();

    const props = defineProps<{
        tabId: number;
    }>();

    const { tabId } = toRefs(props);

    defineExpose({
        tabId,
    });

    const currentSubsetName = ref<string>("default");
    const currentOffset = computed(() => (listPage.value - 1) * listLimit.value);

    const resurser = computed(() => {
        return store.getters["resurs/resursSubset"]({
            subsetName: currentSubsetName.value,
            offset: currentOffset.value,
            limit: listLimit.value,
        }).map((r: ResursType) => ({
            ...r,
            aktiv: r.totalt_antal >= 1,
        }));
    });

    const columns = [
        {
            field: "namn",
            header: "Namn",
        },
        {
            header: "Ordningsnummer",
            field: "ordning",
        },
        {
            header: "Resurstyp",
            field: "typ",
        },
        {
            header: "Tid före salstid start",
            field: "tid_fore_salstid_start",
        },
        {
            header: "Tid efter salstid slut",
            field: "tid_efter_salstid_slut",
        },
        {
            header: "Totalt antal",
            field: "totalt_antal",
        },
        {
            header: "Status",
            field: "aktiv",
            bodyTemplate: CellInaktiv,
        },
    ];

    const openForm = (resurs: ResursType | null = null) => {
        store.dispatch("tabs/closeTab", Resurs);
        store.dispatch("tabs/openTab", { component: Resurs, parameters: resurs });
    };

    const dropdownActions: KTableAction[] = [
        {
            title: "Redigera",
            show: () => true,
            callback: ({ data }) => {
                const resursData = data as ResursType;

                openForm(resursData);
            },
            icon: EditIcon,
        },
    ];

    const listPage = ref<number>(1);
    const listCount = ref<number>(0);
    const listLimit = ref<number>(20);

    const filterParams = ref<{ [key: string]: string | number | boolean } | undefined>();

    const isLoading = ref(false);

    const onPage = async ({ page, rows }: { page: number; rows: number }) => {
        listLimit.value = rows;

        await fetchResurser({ page, limit: rows });
    };

    const fetchResurser = async ({ page, limit = listLimit.value }: { page: number; limit?: number }) => {
        isLoading.value = true;

        try {
            const result = await store.dispatch("resurs/fetchResurser", {
                filterParams: filterParams.value ?? {},
                page,
                limit,
            });

            // Sparar total i listCount.value, även om den är 0
            listCount.value = result?.total ?? listCount.value;

            currentSubsetName.value = result?.subsetName ?? "default";
            listPage.value = page;
        } catch (e) {
            console.error(e);
        }

        isLoading.value = false;
    };

    onBeforeMount(async () => {
        await fetchResurser({ page: listPage.value });
    });

    onBeforeUnmount(() => {
        store.commit("resurs/reset");
    });
</script>

<template>
    <div class="list-resurser">
        <admin-lock-border />
        <h2>
            Resurser
            <hollow-button :icon="PlusCircleBlueIcon" label="Skapa ny resurs" @click="openForm()" />
        </h2>
        <KTable
            paginator
            loadingMessage="Hämtar resurser"
            name="resurs-table"
            :value="resurser"
            :columns="columns"
            :actions="dropdownActions"
            :totalRecords="listCount"
            :defaultRowsPerPage="listLimit"
            :loading="isLoading"
            :selectable-rows="false"
            @page="onPage"
        />
    </div>
</template>

<style lang="scss" scoped>
    h2 {
        .hollow-button {
            margin-left: 50px;
        }
    }

    .list-resurser {
        height: 100%;
    }
</style>
